import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { ArrowLeft, AlertCircle, Loader2, FileText } from "lucide-react";

export default function PDFViewer({ location }) {
    const [pdfUrl, setPdfUrl] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    // No customer popup
    const navigate = useNavigate();

    const isIOS =
        /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    useEffect(() => {
        const fetchLivePDF = async () => {
            try {
                setLoading(true);
                setError("");
                const backendBase =
                    process.env.REACT_APP_MARKETING_BACKEND_URL ||
                    "https://marketing.apnimandi.us/api";

                const response = await fetch(`${backendBase}/pdf/live`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ storeName: location }),
                });

                if (!response.ok) throw new Error("Failed to fetch live PDF");

                const data = await response.json();
                if (!data.url) throw new Error("Invalid PDF URL");

                // 📱 iOS: open PDF directly
                if (isIOS) {
                    window.location.href = data.url;
                    return;
                }

                // 🖥 Others
                setPdfUrl(data.url);
                setLoading(false);

                // Track visit for this store
                fetch(
                    `${
                        process.env.REACT_APP_MARKETING_BACKEND_URL ||
                        "https://marketing.apnimandi.us/api"
                    }/store/${encodeURIComponent(location)}/visit`,
                    { method: "POST" },
                );
            } catch (err) {
                setError(err.message);
                setLoading(false);
            }
        };

        fetchLivePDF();
    }, [location, isIOS]);

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
                {/* Header */}
                <header className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white/80 backdrop-blur-sm shadow-sm">
                    <div className="container mx-auto flex h-20 items-center justify-between px-6">
                        <div className="flex items-center gap-3">
                            <img
                                src="/apnimandi.png"
                                alt="ApniMandi Logo"
                                className="h-14 w-auto object-contain"
                            />
                        </div>
                        <Button
                            variant="ghost"
                            onClick={() => navigate("/")}
                            className="gap-2"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Back to Home
                        </Button>
                    </div>
                </header>

                <div className="container mx-auto flex min-h-[calc(100vh-5rem)] items-center justify-center px-6 py-12">
                    <Card className="w-full max-w-md border-2 border-red-200 bg-red-50">
                        <div className="p-6 text-center">
                            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                                <AlertCircle className="h-8 w-8 text-red-600" />
                            </div>
                            <h2 className="mb-2 text-2xl font-bold text-slate-900">
                                Error Loading PDF
                            </h2>
                            <p className="mb-6 text-slate-600">{error}</p>
                            <Button
                                onClick={() => navigate("/")}
                                className="w-full"
                            >
                                <ArrowLeft className="mr-2 h-4 w-4" />
                                Return to Home
                            </Button>
                        </div>
                    </Card>
                </div>
            </div>
        );
    }

    if (loading || !pdfUrl) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
                {/* Header */}
                <header className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white/80 backdrop-blur-sm shadow-sm">
                    <div className="container mx-auto flex h-20 items-center justify-between px-6">
                        <div className="flex items-center gap-3">
                            <img
                                src="/apnimandi.png"
                                alt="Apni Mandi Logo"
                                className="h-14 w-auto object-contain"
                            />
                            <span className="ml-2 text-2xl font-bold text-primary">
                                Apni&nbsp;Mandi
                            </span>
                        </div>
                        <div className="text-sm font-medium text-slate-600">
                            {location}
                        </div>
                    </div>
                </header>

                <div className="container mx-auto flex min-h-[calc(100vh-5rem)] items-center justify-center px-6">
                    <div className="text-center">
                        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
                            <Loader2 className="h-10 w-10 animate-spin text-primary" />
                        </div>
                        <h2 className="mb-2 text-2xl font-bold text-slate-900">
                            Loading Flyer...
                        </h2>
                        <p className="text-slate-600">
                            Please wait while we fetch the latest offers
                        </p>
                        <button
                            className="mt-6 px-4 py-2 rounded bg-primary text-white font-semibold shadow hover:bg-primary-700"
                            onClick={() => window.location.reload()}
                        >
                            If you can’t see the flyer click here
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Desktop / Android
    return (
        <div className="flex h-screen flex-col bg-slate-50">
            {/* Header */}
            <header className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white shadow-sm">
                <div className="container mx-auto flex h-16 items-center justify-between px-6">
                    <div className="flex items-center gap-4">
                        <Button
                            variant="ghost"
                            onClick={() => navigate("/")}
                            size="sm"
                            className="gap-2"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Back
                        </Button>
                        <div className="h-6 w-px bg-slate-300" />
                        <div className="flex items-center gap-2">
                            <FileText className="h-5 w-5 text-primary" />
                            <span className="text-lg font-semibold text-slate-900">
                                {location} Flyer
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <img
                            src="/apnimandi.png"
                            alt="Apni Mandi Logo"
                            className="h-10 w-auto object-contain"
                        />
                        <span className="ml-2 text-xl font-bold text-primary">
                            Apni&nbsp;Mandi
                        </span>
                    </div>
                </div>
            </header>

            {/* PDF Viewer */}
            <div className="flex-1 overflow-hidden">
                <iframe
                    src={pdfUrl}
                    title={`${location} Flyer`}
                    className="h-full w-full border-0"
                />
            </div>
        </div>
    );
}
